import HiddenInput from "@/components/form/components/HiddenInput";
import { datePickerFormItemProps, datePickerStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { DatePicker, Form, Modal } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateAgentCompletedEducationDetail } from "../../../../common/api/queries";
import { ModalSizes } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import { AgentCompletedEducationDetail, UpdateAgentCompletedEducationDetail } from "../../types";

type ModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  agentId: UUID;
  data: AgentCompletedEducationDetail;
};

export const AgentCompletedEducationDetailModalForm = ({ isOpen, onCancel, agentId, data }: ModalProps) => {
  const { t } = useTranslation();

  const updateAgentCompletedEducationDetail = useUpdateAgentCompletedEducationDetail();

  const [form] = Form.useForm<UpdateAgentCompletedEducationDetail>();

  useEffect(() => {
    if (isOpen && data) {
      form.setFieldsValue(data);
    }
  }, [isOpen, data]);

  const introductionWebinarDate = Form.useWatch("introductionWebinarDate", form);
  const introductionWebinarDatePlusOne = introductionWebinarDate
    ? dayjs(introductionWebinarDate).add(1, "day")
    : undefined;

  const handleFormSubmit = () => {
    form.validateFields().then((values) =>
      updateAgentCompletedEducationDetail.mutate(
        { id: agentId, object: values },
        {
          onSuccess: onCancel,
          onError: (error) => setErrorsToForm(form, "agent.completedEducation.attrs", error.violations),
        },
      ),
    );
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={isOpen}
      confirmLoading={updateAgentCompletedEducationDetail.isPending}
      title={t("agent.education.titles.updateSummaryInfo")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      onCancel={onCancel}
      mask={{ closable: false }}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
    >
      <Form form={form} layout="vertical" name="updateAgentCompletedEducationDetail">
        <HiddenInput name="optimisticLockVersion" />

        <Form.Item
          name="introductionWebinarDate"
          label={t("agent.completedEducation.attrs.introductionWebinarDate")}
          rules={[validations.notFuture, validations.notNull]}
          {...datePickerFormItemProps}
        >
          <DatePicker {...datePickerStandardProps} maxDate={dayjs()} showNow={false} />
        </Form.Item>

        <Form.Item
          name="financialAcademyDate"
          label={t("agent.completedEducation.attrs.financialAcademyDate")}
          rules={[validations.notFuture]}
          {...datePickerFormItemProps}
        >
          <DatePicker
            {...datePickerStandardProps}
            maxDate={dayjs()}
            minDate={introductionWebinarDatePlusOne}
            showNow={false}
            allowClear={true}
          />
        </Form.Item>

        <Form.Item
          name="personalAcademyDate"
          label={t("agent.completedEducation.attrs.personalAcademyDate")}
          rules={[validations.notFuture]}
          {...datePickerFormItemProps}
        >
          <DatePicker
            {...datePickerStandardProps}
            maxDate={dayjs()}
            minDate={introductionWebinarDatePlusOne}
            showNow={false}
            allowClear={true}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
